// "Is this person already in our Customer List?" (Pat, Oct 6 2026: "anytime we enter a person
// to bill, bid, or send submittal correspondence to, double-check they're not already in our
// Customer List, and ask if you'd like to add them to the contact list"). Two pieces:
//
//   <ContactCheck name email phone clientId clientName onUse />
//     A small line under a typed-in person. In the list → "✓ In the Customer List" and, when
//     what's typed differs, "Use their details". Not in it → "Add them to <customer>'s
//     contacts?" (and the customer too, when the company is new).
//
//   <SaveRecipientsPrompt to clientId clientName onClose />
//     After an email goes out: any address on the To/Cc line that isn't on file is offered for
//     the customer's contact list. Shows nothing (and closes itself) when they all are.
import React, { useEffect, useRef, useState } from 'react';
import { apiFetch } from '../services/apiClient';

const chk = body => apiFetch('/contacts/check', { method: 'POST', body: JSON.stringify(body) });
const pill = (bg, fg, bd) => ({ padding: '3px 10px', borderRadius: 999, border: `1px solid ${bd}`, background: bg, color: fg, fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit' });

// "Drew Scolari <dscolari@snc.com>, bob@x.com; ..." → [{ name, email }]
export function parseAddresses(line) {
  return String(line || '').split(/[,;\n]+/).map(s => s.trim()).filter(Boolean).map(s => {
    const m = s.match(/^\s*"?([^"<]*?)"?\s*<\s*([^<>\s]+@[^<>\s]+)\s*>\s*$/);
    if (m) return { name: m[1].trim() || null, email: m[2].toLowerCase() };
    const e = s.match(/([^\s<>"]+@[^\s<>"]+\.[a-z]{2,})/i);
    return e ? { name: null, email: e[1].toLowerCase() } : null;
  }).filter(Boolean);
}
const guessName = email => String(email || '').split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();

// Add one person — and, when the company isn't in the list either, the company first.
async function addPerson({ clientId, clientName, name, email, phone }) {
  let cid = clientId;
  if (!cid) {
    if (!String(clientName || '').trim()) throw new Error('Pick the company first, then add the person.');
    const c = await apiFetch('/clients', { method: 'POST', body: JSON.stringify({ name: String(clientName).trim() }) });
    cid = c.id;
  }
  const contact = await apiFetch(`/clients/${cid}/contacts`, { method: 'POST', body: JSON.stringify({ name: String(name || '').trim() || guessName(email), email: email || null, phone: phone || null }) });
  return { ...contact, client_id: cid };
}

export function ContactCheck({ name, email, phone, clientId = null, clientName = null, onUse = null, onAdded = null, style = null }) {
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [added, setAdded] = useState(null);
  const [err, setErr] = useState('');
  const [dismissed, setDismissed] = useState('');
  const key = `${String(name || '').trim().toLowerCase()}|${String(email || '').trim().toLowerCase()}|${clientId || ''}|${String(clientName || '').trim().toLowerCase()}`;
  const seq = useRef(0);
  useEffect(() => {
    setAdded(null); setErr('');
    const n = String(name || '').trim(), e = String(email || '').trim();
    if (n.length < 3 && !/@/.test(e)) { setRes(null); return; }
    const my = ++seq.current;
    const t = setTimeout(() => {
      chk({ people: [{ name: n, email: e, phone }], client_id: clientId, client_name: clientName })
        .then(r => { if (my === seq.current) setRes(r); }).catch(() => { if (my === seq.current) setRes(null); });
    }, 700);
    return () => clearTimeout(t);
  }, [key]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!res || dismissed === key) return null;
  const r = (res.results || [])[0] || {};
  if (r.empty) return null;
  const box = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12.5, marginTop: 5, ...(style || {}) };
  if (added) return <div style={{ ...box, color: '#15803d', fontWeight: 700 }}>✓ Added {added.name} to {res.client_name || 'the Customer List'}’s contacts.</div>;
  if (r.found) {
    const c = r.contact;
    const differs = (c.email && String(c.email).toLowerCase() !== String(email || '').trim().toLowerCase()) || (c.phone && !String(phone || '').trim()) || (c.name && c.name.toLowerCase() !== String(name || '').trim().toLowerCase());
    return (
      <div style={{ ...box, color: '#166534' }}>
        <span>✓ <b>{c.name}</b> is in the Customer List{c.client_name ? <> under <b>{c.client_name}</b></> : null}{r.other_customer ? ' (a different company than this one)' : ''}{c.email ? ` · ${c.email}` : ''}</span>
        {onUse && differs && <button type="button" onClick={() => onUse(c)} style={pill('#f0fdf4', '#15803d', '#bbf7d0')}>Use their details</button>}
      </div>
    );
  }
  const company = res.client ? res.client.name : res.client_name;
  async function add() {
    setBusy(true); setErr('');
    try { const c = await addPerson({ clientId: res.client && res.client.id, clientName: company, name, email, phone }); setAdded(c); onAdded && onAdded(c); }
    catch (e) { setErr(e?.error || e?.message || 'Could not add them.'); }
    setBusy(false);
  }
  return (
    <div style={{ ...box, color: '#92400e' }}>
      <span>
        {String(name || '').trim() || email} isn’t in the Customer List{res.client ? <> under <b>{res.client.name}</b></> : company ? <> — and <b>{company}</b> isn’t either</> : ''}.
      </span>
      {company
        ? <button type="button" onClick={add} disabled={busy} style={pill('#fffbeb', '#92400e', '#fcd34d')}>{busy ? 'Adding…' : res.client ? 'Add them to the contacts' : `Add ${company} and them`}</button>
        : <span style={{ color: '#9ca3af' }}>Pick the company to add them.</span>}
      <button type="button" onClick={() => setDismissed(key)} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', padding: 0 }}>Not now</button>
      {err && <span style={{ color: '#b91c1c', fontWeight: 700 }}>{err}</span>}
    </div>
  );
}

// After an email is sent. `to` = the To (and Cc) line as typed.
export function SaveRecipientsPrompt({ to, clientId = null, clientName = null, onClose }) {
  const [rows, setRows] = useState(null);   // [{ email, name, add }]
  const [company, setCompany] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    const list = parseAddresses(to).filter(a => !/@titanelectric(reno)?\.biz$/i.test(a.email));
    if (!list.length) { onClose && onClose(); return; }
    chk({ people: list, client_id: clientId, client_name: clientName }).then(r => {
      const missing = list.filter((a, i) => !(r.results[i] || {}).found).map(a => ({ ...a, name: a.name || guessName(a.email), add: true }));
      if (!missing.length) { onClose && onClose(); return; }
      setCompany(r.client ? r.client : (r.client_name ? { id: null, name: r.client_name } : null));
      setRows(missing);
    }).catch(() => onClose && onClose());
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (!rows) return null;
  async function save() {
    setBusy(true); setErr('');
    try {
      let cid = company && company.id;
      for (const r of rows.filter(x => x.add)) { const c = await addPerson({ clientId: cid, clientName: company && company.name, name: r.name, email: r.email }); cid = c.client_id; }
      onClose && onClose();
    } catch (e) { setErr(e?.error || e?.message || 'Could not add them.'); }
    setBusy(false);
  }
  return (
    <div onClick={() => onClose && onClose()} style={{ position: 'fixed', inset: 0, zIndex: 7200, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: 480, maxWidth: '100%', padding: 18, boxShadow: '0 16px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 4 }}>Add to the Customer List?</div>
        <div style={{ fontSize: 13, color: '#6b7280', marginBottom: 12 }}>
          {rows.length === 1 ? 'This address isn’t' : 'These addresses aren’t'} on file.{company ? <> Add {rows.length === 1 ? 'it' : 'them'} to <b>{company.name}</b>’s contacts{company.id ? '' : ' (the company is new to the list too)'}?</> : ' Pick the company on the job first to add them.'}
        </div>
        {rows.map((r, i) => (
          <div key={r.email} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <input type="checkbox" checked={r.add} disabled={!company} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, add: e.target.checked } : x))} />
            <input value={r.name} onChange={e => setRows(rs => rs.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name"
              style={{ flex: '0 0 40%', border: '1px solid #d1d5db', borderRadius: 7, padding: '6px 8px', fontSize: 13 }} />
            <span style={{ fontSize: 13, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</span>
          </div>
        ))}
        {err && <div style={{ color: '#b91c1c', fontSize: 13, fontWeight: 700 }}>{err}</div>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
          <button type="button" onClick={() => onClose && onClose()} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontWeight: 700, cursor: 'pointer' }}>Not now</button>
          {company && <button type="button" onClick={save} disabled={busy || !rows.some(r => r.add)} style={{ padding: '8px 14px', borderRadius: 8, border: 'none', background: '#1e3a5f', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>{busy ? 'Adding…' : 'Add to the contacts'}</button>}
        </div>
      </div>
    </div>
  );
}

// Fire-and-forget from anywhere an email has just gone out: the host in the desktop layout
// shows SaveRecipientsPrompt for the addresses that aren't on file (nothing when they all are).
export function offerToSaveRecipients({ to, cc = '', clientId = null, clientName = null }) {
  try { window.dispatchEvent(new CustomEvent('titan:offer-contacts', { detail: { to: [to, cc].filter(Boolean).join(', '), clientId, clientName } })); } catch { /* a nicety, never in the way */ }
}
export function SaveRecipientsHost() {
  const [offer, setOffer] = useState(null);
  useEffect(() => {
    const on = e => setOffer(o => o || { ...(e.detail || {}), key: Date.now() });
    window.addEventListener('titan:offer-contacts', on);
    return () => window.removeEventListener('titan:offer-contacts', on);
  }, []);
  if (!offer) return null;
  return <SaveRecipientsPrompt key={offer.key} to={offer.to} clientId={offer.clientId} clientName={offer.clientName} onClose={() => setOffer(null)} />;
}
